import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { AxiosResponse } from 'axios';
import { Timelapse } from '@material-ui/icons';
import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { ITableHead, CommonResponse, IGroup, IGlobalState, IImpersonator } from '@app/models';
import {
    PasslyTableRow,
    PasslyTableCell,
    PasslyButton,
    PasslyMaskedInput,
    PasslyTextField
} from '@app/components';
import { usersService } from '@app/services';
import { passlyFormatDate, DATE_TIME_FORMAT, genericErrorToast, digitRegex, calcDays } from '@app/helpers';

enum JITHeadTypes {
    users = 'users',
    start = 'start',
    expiry = 'expiry',
    groupsName = 'groupsName',
    lastModified = 'lastModified'
}

interface IProps {
    handleAddGroups: Function;
    trigger: boolean;
    enabled: boolean;
}

export const JitSection = (props: IProps) => {
    const jitMinConst = 5;
    const jitMaxConst = 2880;
    const { id: userID } = useParams<{ id: string }>();
    const { translation } = useTypedTranslation();
    const { handleAddGroups: addGroupsForJit, trigger, enabled } = props;

    const impersonationWindow: number = useSelector((state: IGlobalState) => state.users.user.ImpersonationWindow);
    const supportsImpersonation: boolean = useSelector((state: IGlobalState) => state.users.user.SupportsImpersonation);

    const [jitMax, setJitMax] = useState<{ old: number, new: number }>(
        {
            old: impersonationWindow,
            new: impersonationWindow
        }
    );
    const [jitReservationEdit, isReservationInEdit] = useState<boolean>(false);
    const [jitEnabled, isJitEnabled] = useState<boolean>(supportsImpersonation || enabled || false);

    const jitReservationHeads: ITableHead[] = [
        {
            id: JITHeadTypes.users,
            label: 'PEOPLE_NAME'
        },
        {
            id: JITHeadTypes.start,
            label: 'JIT_CREATED'
        },
        {
            id: JITHeadTypes.expiry,
            label: 'EXPIRY'
        }
    ];

    const jitGroupsHeads: ITableHead[] = [
        {
            id: JITHeadTypes.groupsName,
            label: 'GROUPS_NAME'
        },
        {
            id: JITHeadTypes.lastModified,
            label: 'GROUPS_LAST_MODIFIED'
        }
    ];

    const [reservationRows, setReservationRows] = useState<any[]>([]);
    const [groupsRows, setGroupsRows] = useState<IGroup[]>([]);

    useEffect(() => {
        getImpersonators();
        getGroups();
    }, []);

    useEffect(() => {
        if (trigger) getGroups();
    }, [trigger]);

    useEffect(() => {
        if (typeof enabled === 'boolean') isJitEnabled(enabled);
    }, [enabled]);

    useEffect(() => {
        if (jitEnabled) {
            getImpersonators();
            getGroups();
        }
    }, [jitEnabled]);

    const getImpersonators = (): void => {
        usersService.getImpersonators(userID, handleImpersSuccess, genericErrorToast);
    };

    const getGroups = (): void => {
        usersService.getImpersonationGroups(userID, handleGroupsSuccess, genericErrorToast);
    };

    const handleGroupRemove = (groupID: string): void => {
        usersService.postRemoveImpGroup(userID, groupID, getGroups, genericErrorToast);
    };

    const handleImpersSuccess = (response: AxiosResponse<CommonResponse<IImpersonator[]>>): void => {
        setReservationRows(response.data.value);
    };

    const handleGroupsSuccess = (response: AxiosResponse<CommonResponse<IGroup[]>>): void => {
        setGroupsRows(response.data.value);
    };

    const handleChangeJitMax = (event: React.ChangeEvent<{ value: unknown }>): void => {
        let value: number = event.target.value as number;
        setJitMax({ ...jitMax, new: value });
    };

    const handleCancel = (): void => {
        setJitMax({ ...jitMax, new: jitMax.old });
        isReservationInEdit(false);
    };

    const handleSave = (): void => {
        const handleSuccess = (): void => {
            setJitMax({ ...jitMax, old: jitMax.new });
            isReservationInEdit(false);
        };
        usersService.postMaxWindow(userID, jitMax.new, handleSuccess, genericErrorToast);
    };

    const expireReservation = (impersonatorID: string): void => {
        usersService.postExpire(userID, impersonatorID, getImpersonators, genericErrorToast);
    };

    const reservationTimeError = (): boolean => {
        return jitMax.new < jitMinConst || jitMax.new > jitMaxConst;
    };

    return (
        <>
            {jitEnabled &&
                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>
                        {translation('JIT_CONFIG_SECTION')}
                    </div>

                    <div className='detailPage__section-content'>
                        <section className='detailPage__block'>
                            <h4 className='detailPage__block-header'>{translation('JIT_CONFIG_LABEL')}</h4>
                            <ul className='detailPage__list'>
                                <li>
                                    <span className='detailPage__list--primary-label'>
                                        {translation('JIT_MAX_WINDOW')}
                                    </span>
                                    <span className='detailPage__list--secondary-label'>
                                        {jitReservationEdit
                                            ?
                                            <>
                                                <PasslyTextField
                                                    fullWidth
                                                    inlineedit
                                                    onClickSave={handleSave}
                                                    onClickDiscard={handleCancel}
                                                    editdisabled={reservationTimeError()}
                                                    label={translation('JIT_MAX_WINDOW_LABEL')}
                                                    id='jitMaxValue'
                                                    name='DisplayName'
                                                    buttonWidth={26}
                                                    buttonFontSize={'medium'}
                                                    onChange={handleChangeJitMax}
                                                    type='text'
                                                    value={jitMax.new}
                                                    error={reservationTimeError()}
                                                    helperText={reservationTimeError() ? '5-2880' : ''}
                                                    InputProps={{
                                                        inputComponent: PasslyMaskedInput,
                                                        error: reservationTimeError(),
                                                        inputProps: {
                                                            maskSize: 4,
                                                            maskRegex: digitRegex
                                                        },
                                                    }}
                                                />
                                            </>
                                            :
                                            <>
                                                <span>{calcDays(jitMax.old)}</span>
                                                <PasslyButton variant='text' color='primary' onClick={() => isReservationInEdit(true)}>{translation('EDIT')}</PasslyButton>
                                            </>
                                        }
                                    </span>
                                </li>
                            </ul>
                        </section>
                        <section className='detailPage__block'>
                            <h4 className='detailPage__block-header'>{translation('JIT_QUEUE_LABEL')}</h4>
                            <span className='detailPage__block-description'>{translation('JIT_QUEUE_DESC')}</span>
                            <TableContainer component={Paper}>
                                <Table aria-label='simple table'>
                                    <TableHead>
                                        <TableRow>
                                            {jitReservationHeads.map((reservation: ITableHead, i: number) => (
                                                <TableCell key={i} id={reservation.id}>{translation(reservation.label)}</TableCell>
                                            ))}
                                            <TableCell>&nbsp;</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reservationRows.map((row: IImpersonator, i: number) => (
                                            <PasslyTableRow key={i}>
                                                <PasslyTableCell>
                                                    <div><b>{row.OnBehalfOf.DisplayName}</b></div>
                                                    <div>{row.OnBehalfOf.PrincipalName}</div>
                                                </PasslyTableCell>
                                                <PasslyTableCell>
                                                    {passlyFormatDate(row.Created, DATE_TIME_FORMAT)}
                                                </PasslyTableCell>
                                                <PasslyTableCell>
                                                    {passlyFormatDate(row.Expiry, DATE_TIME_FORMAT)}
                                                </PasslyTableCell>
                                                <PasslyTableCell>
                                                    <PasslyButton
                                                        variant='contained'
                                                        color='primary'
                                                        onClick={() => expireReservation(row.OnBehalfOfId)}
                                                        startIcon={<Timelapse />}
                                                    >
                                                        {translation('JIT_EXPIRE_RESERVATION')}
                                                    </PasslyButton>
                                                </PasslyTableCell>
                                            </PasslyTableRow>
                                        ))
                                        }
                                        {!reservationRows.length &&
                                            <PasslyTableRow>
                                                <PasslyTableCell colSpan={4} align='center'>
                                                    <span>{translation('EMPTY_IMPERSONATOR_LIST')}</span>
                                                </PasslyTableCell>
                                            </PasslyTableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </section>
                        <section className='detailPage__block'>
                            <h4 className='detailPage__block-header'>{translation('JIT_MEMBER_LABEL')}</h4>
                            <span className='detailPage__block-description'>{translation('JIT_MEMBER_DESC')}</span>
                            <TableContainer component={Paper}>
                                <Table aria-label='simple table'>
                                    <TableHead>
                                        <TableRow>
                                            {jitGroupsHeads.map((group: ITableHead, i: number) => (
                                                <TableCell key={i} id={group.id}>{translation(group.label)}</TableCell>
                                            ))}
                                            <TableCell>&nbsp;</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groupsRows.map((group: IGroup, i: number) => (
                                            <PasslyTableRow key={i}>
                                                <PasslyTableCell>
                                                    {group.Name}
                                                </PasslyTableCell>
                                                <PasslyTableCell>
                                                    {passlyFormatDate(group.LastModified, DATE_TIME_FORMAT)}
                                                </PasslyTableCell>
                                                <PasslyTableCell>
                                                    <PasslyButton
                                                        variant='text'
                                                        onClick={() => handleGroupRemove(group.Id)}
                                                    >
                                                        {translation('REMOVE')}
                                                    </PasslyButton>
                                                </PasslyTableCell>
                                            </PasslyTableRow>
                                        ))
                                        }
                                        {!groupsRows.length &&
                                            <PasslyTableRow>
                                                <PasslyTableCell colSpan={4} align='center'>
                                                    <span>{translation('EMPTY_GROUP_LIST')}</span>
                                                </PasslyTableCell>
                                            </PasslyTableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className='detailPage__button_container'>
                                <PasslyButton
                                    id='add-group-for-jit'
                                    variant='contained'
                                    color='primary'
                                    onClick={() => addGroupsForJit()}
                                >
                                    {translation('ADD_GROUP')}
                                </PasslyButton>
                            </div>
                        </section>
                    </div>
                </div>

            }
        </>
    );
};
